import type { RouteLocationRaw } from 'vue-router';

import { i18n } from '@/plugins/i18n';
import type {
  FormDto,
  LocalizableString,
  ProjectModuleDto,
  ProjectModuleEventItemConfigDto,
  ProjectModuleNewsItemConfigDto,
  ProjectDto,
  SectionDto,
  Locales,
} from '@/services/types/api.types';
import { getModuleDisplayTitleWithFallback } from '@/composables/data-display-utils';
import type { BlockLabels } from '@/composables/useBlockParent';
import { blockTypeName } from '@/helpers/block.helper';
import type { BlockRouteFor, ModuleTypeOption } from '@/types/modules';

const t = i18n.global.t;

export interface BreadcrumbDropdownOption {
  id: number | undefined;
  label: LocalizableString | string | undefined;
  to: RouteLocationRaw | undefined;
  icon?: string;
}

export interface BreadcrumbElement {
  label: LocalizableString | string | undefined;
  to: RouteLocationRaw | undefined;
  icon?: string;
  options?: BreadcrumbDropdownOption[];
  activeId?: number;
}

type MappedOption<T> = BreadcrumbDropdownOption | null | undefined;

type Mapper<T> = (item: T) => MappedOption<T>;

export function mapToBreadcrumbDropdownOptions<T>(items: T[], mapper: Mapper<T>): BreadcrumbDropdownOption[] {
  return items.reduce<BreadcrumbDropdownOption[]>((options, item) => {
    const option = mapper(item);
    if (option && option.id !== undefined && option.to !== undefined) {
      options.push(option);
    }
    return options;
  }, []);
}

export function createSectionBreadcrumbDropdownOptions(
  sections: SectionDto[],
  isTemplate: boolean,
): BreadcrumbDropdownOption[] {
  const options = mapToBreadcrumbDropdownOptions(sections, (section) => {
    if (!section.id || !section.consultation_id) return null;
    return {
      id: section.id,
      label: section.title,
      to: {
        name: isTemplate ? 'section_template_details' : 'section_details',
        params: isTemplate ? { consultationId: section.consultation_id, id: section.id } : { id: section.id },
      },
      icon: 'o_folder',
    };
  });
  if (options.length === 0) {
    options.push({
      id: undefined,
      label: t('breadcrumbs.no-sections'),
      to: undefined,
    });
  }
  return options;
}

export function createFormBreadcrumbDropdownOptions(
  consultationId: number | undefined,
  forms: FormDto[],
  isTemplate: boolean,
): BreadcrumbDropdownOption[] {
  const options = mapToBreadcrumbDropdownOptions(forms, (form) => {
    if (!consultationId || !form.id || !form.section_id) return null;
    return {
      id: form.id,
      label: form.question?.title,
      to: {
        name: isTemplate ? 'form_template_details' : 'form_details',
        params: isTemplate ? { consultationId, sectionId: form.section_id, id: form.id } : { id: form.id },
      },
      icon: 'o_article',
    };
  });
  if (options.length === 0) {
    options.push({
      id: undefined,
      label: t('breadcrumbs.no-forms'),
      to: undefined,
    });
  }
  return options;
}

export function createModuleBreadcrumbDropdownOptions(
  modules: ProjectModuleDto[],
  blockRouteFor: BlockRouteFor,
  blockLabels: BlockLabels,
  blockTypes: ModuleTypeOption[],
  project?: ProjectDto | null,
  languageEnabled?: Locales[],
): BreadcrumbDropdownOption[] {
  const options = mapToBreadcrumbDropdownOptions(modules, (module) => {
    if (!module.id) return null;
    return {
      id: module.id,
      label: getModuleDisplayTitleWithFallback(
        module,
        project,
        languageEnabled,
        blockTypeName(blockTypes, module.block_type),
      ),
      to: blockRouteFor(module.id),
      icon: blockLabels.icon,
    };
  });
  if (options.length === 0) {
    options.push({
      id: undefined,
      label: t(blockLabels.noBlocksKey),
      to: undefined,
    });
  }
  return options;
}

export function createEventItemsBreadcrumbDropdownOptions(
  eventItems: ProjectModuleEventItemConfigDto[],
  project_id?: number,
): BreadcrumbDropdownOption[] {
  const options = mapToBreadcrumbDropdownOptions(eventItems, (eventItem) => {
    if (!eventItem.id) return null;
    return {
      id: eventItem.id,
      label: eventItem.title,
      to: {
        name: 'event_item_details',
        params: { id: eventItem.id, projectId: project_id, moduleId: eventItem.project_module_id },
      },
      icon: 'o_event',
    };
  });
  if (options.length === 0) {
    options.push({
      id: undefined,
      label: t('breadcrumbs.no-event-items'),
      to: undefined,
    });
  }
  return options;
}

export function createNewsItemsBreadcrumbDropdownOptions(
  newsItems: ProjectModuleNewsItemConfigDto[],
  project_id?: number,
): BreadcrumbDropdownOption[] {
  const options = mapToBreadcrumbDropdownOptions(newsItems, (newsItem) => {
    if (!newsItem.id) return null;
    return {
      id: newsItem.id,
      label: newsItem.title,
      to: {
        name: 'news_item_details',
        params: { id: newsItem.id, projectId: project_id, moduleId: newsItem.project_module_id },
      },
      icon: 'o_event',
    };
  });
  if (options.length === 0) {
    options.push({
      id: undefined,
      label: t('breadcrumbs.no-news-items'),
      to: undefined,
    });
  }
  return options;
}
